import React, { useContext, useEffect, useRef } from "react";
import { FieldProps } from "@rjsf/utils";
import { get, isNil } from "lodash";
import { cloneDeep } from "lodash";
import { FileRejection } from "react-dropzone";
import { ErrorCode } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { MIME_TYPE } from "common/constants";
import { MAX_SIZE_PER_MIME_TYPE_IN_MB } from "common/utils/documentUploads";
import { getUserErrors } from "common-client/utils/apollo";
import {
  CancelationReason,
  CertificateUploadStatus,
  useGeneratePresignedUrlMutation,
  useValidateScratchDocumentUploadMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import type { colors } from "../../../stitches.config";
import { ViewDocumentLink } from "../../AddressPanel/DocumentUploads/ViewDocumentLink";
import { AuthContext } from "../../Authorization/AuthContext";
import { FlexRow } from "../../Common/__styles__/Layout";
import { ScreenOnly } from "../../Common/__styles__/ScreenOnly";
import { DynamicOverflowWrapper } from "../../Common/DynamicOverflowWrapper";
import { EmptyState } from "../../Common/EmptyState";
import {
  Icon,
  Icon as LucideIcon,
  IconNames,
} from "../../Common/Icons/LucideIcons";
import { Tag } from "../../Common/Tag";
import {
  Block,
  Info,
} from "../../DocumentUploads/__styles__/UploadDocumentsForm";
import { CANCELATION_REASON_TEXT } from "../../DocumentUploads/Settings";
import { DropzoneComponent } from "../../FileUploads";
import { EmptyFileContainer } from "../../FileUploads/__styles__/FileUploads";
import Wrapper from "../../Inputs/Wrapper";
import { Input } from "../__styles__/Inputs";
import { SubmissionFormContext } from "../Form";
import {
  AdditionalInfo,
  BrowseStyling,
  DropzonePositioner,
  FileNameLinkWrapper,
  IconPositioner,
  OverflowText,
  Table,
  TableData,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TagWrapper,
} from "./__styles__/DocumentUploadField";

type DocumentUploadStatus =
  | CertificateUploadStatus
  | "valid"
  | "invalid"
  | "loading";

export interface DocumentUpload {
  id: Maybe<string>;
  scratchId?: string;
  originalFilename?: string;
  mimetype?: MIME_TYPE;
  status?: "valid" | "invalid" | "loading";
  hiddenFromPublic?: boolean;
}

const getIconProps = ({
  documentStatus,
  formDataValue,
}: {
  documentStatus?: Maybe<DocumentUploadStatus>;
  formDataValue: DocumentUpload;
}): {
  iconName: IconNames;
  color: keyof typeof colors;
  "aria-labelledby"?: string;
} => {
  if (isNil(formDataValue.id)) {
    switch (documentStatus) {
      case "valid":
        return {
          iconName: "check-circle",
          color: "contentSuccess",
          "aria-labelledby": "checkCircle",
        };
      case "invalid":
        return {
          iconName: "x-circle",
          color: "contentCritical",
          "aria-labelledby": "xCircle",
        };
      case "loading":
        return {
          iconName: "loading",
          color: "contentPlaceholder",
          "aria-labelledby": "loading",
        };
      default:
        break;
    }
  }

  return (
      documentStatus === CertificateUploadStatus.CANCELED ||
        documentStatus === CertificateUploadStatus.PROCESSING
    ) ?
      {
        iconName: "info",
        color: "contentAttention",
        "aria-labelledby": "info",
      }
    : {
        iconName: "check-circle",
        color: "contentSuccess",
        "aria-labelledby": "checkCircle",
      };
};
const FileNameCell = ({
  fileName,
  status,
  cancelationReason,
  documentUploadId,
  hiddenFromPublic,
  externalDocumentURL,
}: {
  fileName: string;
  status?: DocumentUploadStatus;
  cancelationReason?: Maybe<CancelationReason>;
  documentUploadId?: Maybe<string>;
  hiddenFromPublic?: boolean;
  externalDocumentURL?: Maybe<string>;
}) => {
  const { account, isPublic } = useContext(AuthContext);

  const isProcessing = status === CertificateUploadStatus.PROCESSING;
  const reasonText = CANCELATION_REASON_TEXT[cancelationReason ?? "default"];

  const fileNameLink =
    documentUploadId && !isProcessing ?
      <DynamicOverflowWrapper
        Link={ViewDocumentLink}
        linkProps={{
          documentUploadId,
          externalDocumentURL,
          isDocumentUploadField: true,
        }}
        text={fileName}
      />
    : <OverflowText>{fileName}</OverflowText>;

  return (
    <TableData>
      <>
        <FlexRow>
          <FileNameLinkWrapper>{fileNameLink}</FileNameLinkWrapper>
          {!isNil(hiddenFromPublic) &&
            !isPublic &&
            status !== "loading" &&
            account?.publicPortal.enabled && (
              <ScreenOnly>
                <TagWrapper>
                  <Tag styleVariant="neutral">
                    {hiddenFromPublic ?
                      "Hidden on public website"
                    : "Displayed on public website"}
                  </Tag>
                </TagWrapper>
              </ScreenOnly>
            )}
        </FlexRow>
        {(status === CertificateUploadStatus.CANCELED ||
          status === CertificateUploadStatus.PROCESSING) && (
          <AdditionalInfo>
            {status === CertificateUploadStatus.CANCELED ?
              reasonText
            : "File processing..."}
          </AdditionalInfo>
        )}
      </>
    </TableData>
  );
};

const ExistingDocuments = ({
  documentUploadValues,
  setDocumentUploadValues,
  onChange,
  disabled,
  emptyFilesMessage,
}: {
  documentUploadValues: Array<DocumentUpload>;
  setDocumentUploadValues: (args: Array<DocumentUpload> | undefined) => void;
  onChange: any;
  disabled: boolean;
  emptyFilesMessage?: string;
}) => {
  const { isPublic } = useContext(AuthContext);
  const { documentUploads } = useContext(SubmissionFormContext);

  const documentUploadValuesToDisplay =
    isPublic ?
      documentUploadValues.filter(documentUpload => {
        const existingDocumentUpload = documentUploads?.find(
          existingDocumentUpload =>
            existingDocumentUpload.id === documentUpload.id,
        );
        return !(existingDocumentUpload?.hiddenFromPublic ?? false);
      })
    : documentUploadValues;

  if (!documentUploadValuesToDisplay.length) {
    return disabled ?
        <EmptyState
          compact={true}
          title={emptyFilesMessage || "No files uploaded"}
        />
      : null;
  }

  return (
    <>
      <Table css={{ marginTop: "15px" }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell css={{ "@media print": { display: "none" } }} />
            <TableHeaderCell alignLeft={true}>File name</TableHeaderCell>
            {!disabled && (
              <TableHeaderCell css={{ "@media print": { display: "none" } }}>
                Remove
              </TableHeaderCell>
            )}
          </TableRow>
        </TableHeader>
        <tbody>
          {documentUploadValuesToDisplay.map((formDataValue, index) => {
            const existingDocumentUpload = documentUploads?.find(
              documentUpload => formDataValue.id === documentUpload.id,
            );

            const status =
              existingDocumentUpload?.certificateUpload?.status ??
              formDataValue.status;
            const iconProps = getIconProps({
              documentStatus: status,
              formDataValue,
            });

            return (
              <TableRow key={index}>
                <TableData
                  css={{ width: "35px", "@media print": { display: "none" } }}
                >
                  <IconPositioner loading={status === "loading"} status>
                    <LucideIcon
                      size={16}
                      testId="document-status-icon"
                      {...iconProps}
                    />
                  </IconPositioner>
                </TableData>
                <FileNameCell
                  status={status}
                  cancelationReason={
                    existingDocumentUpload?.certificateUpload?.cancelationReason
                  }
                  fileName={
                    (existingDocumentUpload?.originalFilename ??
                      formDataValue.originalFilename)!
                  }
                  documentUploadId={existingDocumentUpload?.id}
                  externalDocumentURL={
                    existingDocumentUpload?.externalDocumentURL ?? null
                  }
                  hiddenFromPublic={
                    existingDocumentUpload?.hiddenFromPublic ??
                    formDataValue.hiddenFromPublic
                  }
                />
                <TableData
                  css={{
                    height: "16px",
                    width: "16px",
                    textAlign: "right",
                  }}
                  printOnly
                >
                  {status !== CertificateUploadStatus.PROCESSING &&
                    !disabled && (
                      <IconPositioner remove>
                        <Icon
                          iconName="x-circle"
                          color="contentSecondary"
                          size={16}
                          testId="remove-document"
                          onClick={() => {
                            let modifiedUploads:
                              | Array<DocumentUpload>
                              | undefined = cloneDeep(documentUploadValues);
                            modifiedUploads.splice(index, 1);

                            if (modifiedUploads.length === 0) {
                              modifiedUploads = undefined;
                            }

                            setDocumentUploadValues(modifiedUploads);
                            onChange(modifiedUploads);
                          }}
                        />
                      </IconPositioner>
                    )}
                </TableData>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

const Information = (props: FieldProps) => {
  return (
    <Info>
      <Block icon={"limit"}>{props.uiSchema!["ui:fileTypeDescription"]}</Block>
      <Block icon={"pages"}>
        {props.uiSchema!["ui:fileQuantityDescription"]}
      </Block>
      <Block icon={"notify"}>{props.uiSchema!["ui:fileSizeDescription"]}</Block>
    </Info>
  );
};

export const DocumentUploadInput = (props: FieldProps) => {
  const { account } = React.useContext(AuthContext);
  const { addErrorToast } = useStatusToasts();

  let existingFormData = props.formData?.filter(
    (value: DocumentUpload | Object) => Object.keys(value).length !== 0,
  );

  if (!existingFormData?.length) {
    existingFormData = undefined;
  }

  const [documentUploadValues, setDocumentUploadValues] = React.useState<
    Array<DocumentUpload & { clientKey?: string }> | undefined
  >(existingFormData);
  const documentUploadValuesRef = useRef(documentUploadValues);

  useEffect(() => {
    documentUploadValuesRef.current = documentUploadValues;
  }, [documentUploadValues]);

  const [generatePresignedUrl] = useGeneratePresignedUrlMutation({});
  const [validateScratchDocumentUpload] =
    useValidateScratchDocumentUploadMutation();

  const DropzoneContent = () => (
    <>
      <h5>Drop your {props.uiSchema!["ui:documentTypeName"]} here</h5>
      <p>
        or <BrowseStyling>browse</BrowseStyling> to choose an{" "}
        {props.uiSchema!["ui:documentTypeAbbreviation"]}
      </p>
    </>
  );

  const accountDocumentTypeId = get(
    props.schema,
    "items.properties.accountDocumentTypeId.enum",
    [],
  )[0];
  const accountDocumentType = account?.accountDocumentTypes.find(
    documentType => accountDocumentTypeId === documentType.id,
  );

  const maxFiles = props.schema.maxItems;

  async function onDrop(files: Array<File & { clientKey?: string }>) {
    if (
      maxFiles &&
      files.length + (documentUploadValues?.length ?? 0) > maxFiles
    ) {
      const fileText = maxFiles === 1 ? "file" : "files";
      addErrorToast(`Only ${maxFiles} ${fileText} can be uploaded at a time.`);
    }

    files.forEach(file => {
      file.clientKey = uuidv4();
    });

    const newFormValue = [
      ...(documentUploadValues ?? []),
      ...files.map(file => ({
        ...file,
        id: null,
        accountDocumentTypeId,
        originalFilename: file.name,
        mimetype: file.type as MIME_TYPE,
        status: "loading" as const,
        hiddenFromPublic: accountDocumentType?.hiddenFromPublicByDefault,
      })),
    ];

    setDocumentUploadValues(newFormValue);
    props.onChange(newFormValue);

    for (const file of files) {
      await generatePresignedUrl({
        variables: {
          data: {
            accountDocumentTypeId,
            clientMimeType: file.type,
            clientKey: file.clientKey!,
          },
        },
        onCompleted: async data => {
          let result: Response;
          try {
            result = await fetch(data.generatePresignedUrl.presignedUrl, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": file.type },
            });
          } catch (error) {
            const isNetworkError =
              error instanceof TypeError &&
              /NetworkError|Failed to fetch|Load failed/.test(error.message);
            if (isNetworkError) {
              const fileToUpdate = documentUploadValuesRef.current?.find(
                f => f.clientKey === data.generatePresignedUrl.clientKey,
              );
              if (fileToUpdate) {
                fileToUpdate.status = "invalid";
                setDocumentUploadValues([...documentUploadValuesRef.current!]);
                props.onChange(documentUploadValuesRef.current);
              }
              addErrorToast(`Failed to upload ${file.name}. Please try again.`);
              return;
            }
            throw error;
          }

          if (!result.ok) {
            throw new Error(`Received a ${result.status} when uploading file`);
          }
          await validateScratchDocumentUpload({
            variables: {
              scratchId: data.generatePresignedUrl.scratchId,
              accountDocumentTypeId,
              mimetype: file.type as MIME_TYPE,
            },
            onCompleted: () => {
              const fileToUpdate = documentUploadValuesRef.current?.find(
                f => f.clientKey === data.generatePresignedUrl.clientKey,
              );
              if (fileToUpdate) {
                fileToUpdate.status = "valid";
                fileToUpdate.scratchId = data.generatePresignedUrl.scratchId;
                setDocumentUploadValues(documentUploadValuesRef.current);
                props.onChange(documentUploadValuesRef.current);
              }
            },
            onError: error => {
              const fileToUpdate = documentUploadValuesRef.current?.find(
                f => f.clientKey === data.generatePresignedUrl.clientKey,
              );
              if (fileToUpdate) {
                fileToUpdate.status = "invalid";
                setDocumentUploadValues(documentUploadValuesRef.current);
                props.onChange(documentUploadValuesRef.current);
              }
              const userError = getUserErrors(error);
              if (userError) {
                addErrorToast(userError);
              } else {
                addErrorToast(
                  `Failed to validate ${file.name}. Please try again.`,
                );
              }
            },
          });
        },
        onError: () => {
          const fileToUpdate = documentUploadValuesRef.current?.find(
            f => f.clientKey === file.clientKey,
          );
          if (fileToUpdate) {
            fileToUpdate.status = "invalid";
            setDocumentUploadValues(documentUploadValuesRef.current);
            props.onChange(documentUploadValuesRef.current);
          }
        },
      });
    }
  }

  const onDropRejected = (fileWithErrors: Array<FileRejection>) => {
    fileWithErrors.forEach(fileWithError => {
      const errorCodesForFile = fileWithError.errors.map(error => error.code);
      if (errorCodesForFile.includes(ErrorCode.FileTooSmall)) {
        addErrorToast(`${fileWithError.file.name} is an empty file`);
      }
      if (errorCodesForFile.includes(ErrorCode.FileTooLarge)) {
        addErrorToast(`${fileWithError.file.name} is too large`);
      }
      if (errorCodesForFile.includes(ErrorCode.FileInvalidType)) {
        addErrorToast(`${fileWithError.file.name} is the wrong file type`);
      }
    });

    if (
      fileWithErrors.some(file =>
        file.errors.some(error => error.code.includes(ErrorCode.TooManyFiles)),
      )
    ) {
      const fileText = maxFiles === 1 ? "file" : "files";
      addErrorToast(`Only ${maxFiles} ${fileText} can be uploaded at a time.`);
    }
  };

  const shouldShowDropzone =
    !props.disabled &&
    !props.readonly &&
    maxFiles &&
    (documentUploadValues?.length ?? 0) < maxFiles;

  return (
    <Input id={props.idSchema.$id}>
      <Wrapper
        name={props.name}
        required={props.required && !props.disabled && !props.readonly}
        label={props.schema.title}
        labelSize={props.uiSchema?.["ui:labelSize"]}
        helperText={props.uiSchema?.["ui:helperText"]}
        error={props.rawErrors?.join(", ")}
        tooltip={props.uiSchema?.["ui:tooltipText"]}
      >
        <div style={{ width: "100%" }}>
          {shouldShowDropzone && (
            <EmptyFileContainer>
              <DropzonePositioner>
                <DropzoneComponent
                  border={false}
                  onDrop={onDrop}
                  onDropRejected={onDropRejected}
                  maxSizePerMimeType={MAX_SIZE_PER_MIME_TYPE_IN_MB}
                  maxFiles={maxFiles}
                  allowedMimeTypes={
                    accountDocumentType?.allowedMimeTypes ?? [MIME_TYPE.PDF]
                  }
                  content={DropzoneContent}
                />
              </DropzonePositioner>
              {!documentUploadValues?.length && <Information {...props} />}
            </EmptyFileContainer>
          )}

          <ExistingDocuments
            onChange={props.onChange}
            documentUploadValues={documentUploadValues ?? []}
            setDocumentUploadValues={setDocumentUploadValues}
            disabled={props.disabled || props.readonly}
            emptyFilesMessage={props.uiSchema!["ui:emptyFilesMessage"]}
          />
        </div>
      </Wrapper>
    </Input>
  );
};
